import { useCallback, useMemo } from 'react'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import {
  ConnectorType,
  ConnectorTypeConnectorDirection,
  ConnectorTypeRuntimeVersion,
} from '@mapped/schema-graph-react-apollo'
import { Services } from '../services'

/**
 * Connector *type* metadata — name, direction, runtime version, and the two
 * URLs derived from them (the config UI's base and the logo).
 *
 * Three things here are corrections rather than style, and all three were
 * costing real work on every render of every table that shows a connector.
 *
 * 1. **`cache-first`.** `connectorTypes` is a deployment-scoped catalogue: it
 *    changes when a connector type ships, not while a user is looking at a
 *    grid. Under `cache-and-network` every mount re-fetched it and every
 *    response replaced `internalData`, which rebuilt everything below.
 *
 * 2. **Stable identities.** `detailedConnectorTypes`, `getConnectorDetails`,
 *    `getBaseUrl` and `getLogoUrl` were all rebuilt on every render.
 *    `ConnectorsTable` puts `getConnectorDetails` in the dependency list of its
 *    column model, so a new closure per render re-initialised the grid's entire
 *    column definition per render. `getBaseUrl`/`getLogoUrl` close over nothing
 *    but `Services.connectors.client_uri`, which is module state, so they are
 *    genuinely constant.
 *
 * 3. **`loading` reflects the request, not the result.** It used to be
 *    `!detailedConnectorTypes?.length`, which stays true forever if the query
 *    fails or the org has no connector types — and `useConnectors` renders
 *    `connectors: loadingDetails ? [] : connectors`, so a single failed
 *    catalogue request emptied the connectors page permanently with no error
 *    anywhere. A finished-but-empty catalogue now yields `loading: false` and
 *    connectors that render with the fallback mark.
 *
 * Not to be mounted inside a `renderCell`: it is a query hook, and one instance
 * per visible cell is one subscription per visible cell. The explore grid mounts
 * it once, in `ConnectorLookupProvider`, and passes `getLogoUrl` down.
 */
export function useConnectorDetails() {
  const { data: internalData, loading: internalLoading } = useQuery<{
    connectorTypes: ConnectorType[]
  }>(INTERNAL_CONNECTOR_TYPES_QUERY, {
    fetchPolicy: 'cache-first',
  })

  const getBaseUrl = useCallback((runtimeVersion: any, connectorTypeId: any) => {
    const baseUrl = Services.connectors.client_uri

    if (runtimeVersion === ConnectorTypeRuntimeVersion.V3) {
      // Every cv3 connector serves its UI under <origin>/connector/<type>.
      // Derive the origin from client_uri and append the type on ALL
      // environments — including localhost, where a single reverse proxy
      // (connectors-proxy) fans one origin out to each runtime by path. This
      // lets one CONNECTORS_CLIENT_URI serve every connector; previously the
      // localhost branch returned client_uri verbatim, so only one connector's
      // config UI could be reached locally.
      const origin = new URL(baseUrl).origin

      return `${origin}/connector/${connectorTypeId}`
    }

    return baseUrl
  }, [])

  const getLogoUrl = useCallback(
    (runtimeVersion: any, connectorTypeId: any, isMappedSpecialType: any) => {
      if (isMappedSpecialType) {
        return `/img/mapped-connector-logo.png`
      }

      const baseUrl = getBaseUrl(runtimeVersion, connectorTypeId)

      if (runtimeVersion === ConnectorTypeRuntimeVersion.V3) {
        return `${baseUrl}/logo`
      }

      return `${baseUrl}/api/logo?connectorTypeId=${connectorTypeId}`
    },
    [getBaseUrl]
  )

  const detailedConnectorTypes = useMemo(
    () =>
      internalData?.connectorTypes?.map((type) => {
        const isMappedSpecialType = MAPPED_SPECIAL_CONNECTOR_TYPES.includes(
          type.id!
        )

        return {
          connectorTypeId: type.id,
          isMappedSpecialType,
          name: type.name,
          description: type.description,
          direction: type.direction,
          runtimeVersion: type.runtimeVersion,
          logo: getLogoUrl(type.runtimeVersion, type.id, isMappedSpecialType),
          baseUrl: getBaseUrl(type.runtimeVersion, type.id),
        }
      }),
    [internalData?.connectorTypes, getBaseUrl, getLogoUrl]
  )

  // A Map rather than the `.find()` this used to do on every call. The connector
  // grid calls it once per rendered row, which was O(rows x types) per paint.
  const byConnectorTypeId = useMemo(
    () =>
      new Map(
        (detailedConnectorTypes ?? []).map((type) => [type.connectorTypeId, type])
      ),
    [detailedConnectorTypes]
  )

  const getConnectorDetails = useCallback(
    (connectorTypeId: string) => byConnectorTypeId.get(connectorTypeId),
    [byConnectorTypeId]
  )

  return {
    loading: internalLoading,
    getConnectorDetails,
    detailedConnectorTypes,
    getBaseUrl,
    getLogoUrl,
  }
}

export const MAPPED_SPECIAL_CONNECTOR_TYPES = [
  'mapped-admin-center',
  'mapped-expert-center',
  'ANAXI_CONN_TYPE',
  'MAPPED_SPACE_DESIGNER',
  'mapped-space-designer',
]

const INTERNAL_CONNECTOR_TYPES_QUERY = gql`
  query connectorTypes {
    connectorTypes {
      id
      name
      description
      direction
      runtimeVersion
    }
  }
`

export interface IConnectorDetails {
  connectorTypeId: string
  name?: string
  description?: string
  logo?: string
  direction?: ConnectorTypeConnectorDirection
  runtimeVersion?: ConnectorTypeRuntimeVersion
  baseUrl?: string
}
